'use client';

import { c as _c } from "react/compiler-runtime";
import { ChainUtils } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, type InterfaceInputToken, type Point, type Rewards, VaultToken, YieldBearingToken, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { AvatarMultichain, InfoTooltip } from '@morpho-org/ui-v6/client';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Badge, Button, Icon, Separator } from '@repo/ui';
import { ArrowDiagonalUp, ArrowUpToLine, CirclePlus, Dollar, InterestGenerated, Points, RewardsGradient, Rewards as RewardsIcon, WarningTriangleSolid } from '@repo/ui/icons';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { formatFeePercent, formatMaxApy, formatVaultApy } from '@/helpers/formatters';
import { mapVaultApyCardRows } from '@/helpers/vaultApyCardRows';
import { getVaultV2RateCapTooltip, VAULT_V2_INTEREST_PAUSED_LABEL, VAULT_V2_INTEREST_PAUSED_TOOLTIP } from '@/helpers/vaultV2RateCap';
import type { CollateralRewardEntry } from '../CollateralYieldTags';
import { PointInfoHoverCard } from '../PointInfoHoverCard';
import { VaultV2MaxRateWarning } from '../VaultV2MaxRateWarning';
import NativeYield from './NativeYield/NativeYield';
export interface RewardsHoverCardProps {
  rewards?: Rewards[] | null;
  apy?: bigint | null;
  netApy?: bigint | null;
  performanceFee?: bigint | null;
  managementFee?: bigint | null;
  variant: 'market' | 'vault' | 'supply';
  points?: Point[];
  asset?: InterfaceInputToken | null;
  durationText?: string;
  maxRate?: bigint | null;
  /** Gross APY cap as a fraction (e.g. 0.284), used for the "Capped at" row. */
  maxApy?: number | null;
  /** Whether the vault's gross APY is currently pinned to its cap. */
  isRateCapped?: boolean;
  /** Whether the vault's yield includes donations (manually tagged, see config/donation-vaults). */
  includesDonations?: boolean;
  /** Presence, including null, identifies the V2 APY-card data source. */
  apyBreakdown?: VaultV2ApyBreakdown | null;
  hideNetApyHeader?: boolean;
  instantApy?: bigint | null;
  dailyApy?: bigint | null;
  weeklyApy?: bigint | null;
  /**
   * Display-only reward APRs earned for holding the market's collateral asset.
   * Rendered alongside the collateral's native yield, asset-level — never
   * folded into the net rate.
   */
  collateralRewards?: CollateralRewardEntry[] | null;
}
const rewardFormatter = formatters.percent.digits(2).unit('%').sign().max(10);

// Collateral rewards mirror the native-yield row: a plain percentage, no sign
// (unlike borrow-side rewards, which can be negative and keep their sign).
const collateralRewardFormatter = formatters.percent.digits(2).unit('%').max(10);
const rowClassName = 'flex min-h-8 items-center justify-between';
const RewardsHoverCard = t0 => {
  const $ = _c(162);
  const {
    rewards,
    netApy,
    apy,
    performanceFee,
    managementFee,
    variant,
    points,
    asset,
    durationText,
    maxRate,
    maxApy,
    isRateCapped,
    includesDonations,
    apyBreakdown,
    hideNetApyHeader,
    instantApy,
    dailyApy,
    weeklyApy,
    collateralRewards
  } = t0;
  let t1;
  if ($[0] !== performanceFee) {
    t1 = formatFeePercent(performanceFee);
    $[0] = performanceFee;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedPerformanceFee = t1;
  let t2;
  if ($[2] !== managementFee) {
    t2 = formatFeePercent(managementFee);
    $[2] = managementFee;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formattedManagementFee = t2;
  const isVault = variant === "vault";
  const isVaultV2 = isVault && apyBreakdown !== undefined;
  const headerLabel = variant === "market" ? "Net Rate" : "Net APY";
  const breakdownLabel = isVaultV2 ? "Native APY" : isVault ? "Vault Net APY" : variant === "market" ? "Native Rate" : "Native APY";
  let apyCardRows;
  let formattedMaxApy;
  let netApyRow;
  let showCappedRow;
  let showDonationRow;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== apy || $[5] !== apyBreakdown || $[6] !== asset || $[7] !== breakdownLabel || $[8] !== collateralRewards || $[9] !== dailyApy || $[10] !== durationText || $[11] !== formattedManagementFee || $[12] !== formattedPerformanceFee || $[13] !== headerLabel || $[14] !== hideNetApyHeader || $[15] !== includesDonations || $[16] !== instantApy || $[17] !== isRateCapped || $[18] !== isVault || $[19] !== isVaultV2 || $[20] !== managementFee || $[21] !== maxApy || $[22] !== maxRate || $[23] !== netApy || $[24] !== points?.length || $[25] !== rewards || $[26] !== variant || $[27] !== weeklyApy) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const activeCollateralRewards = collateralRewards?.filter(_temp) ?? [];
      const hasCollateralRewards = activeCollateralRewards.length > 0;
      let t16;
      if ($[46] !== dailyApy || $[47] !== instantApy || $[48] !== variant || $[49] !== weeklyApy) {
        t16 = variant !== "vault" && (isDefined(instantApy) || isDefined(dailyApy) || isDefined(weeklyApy));
        $[46] = dailyApy;
        $[47] = instantApy;
        $[48] = variant;
        $[49] = weeklyApy;
        $[50] = t16;
      } else {
        t16 = $[50];
      }
      const hasRateBreakdown = t16;
      if (!isVaultV2 && !isDefined(apy) && !hasRateBreakdown) {
        t15 = null;
        break bb0;
      }
      let t17;
      if ($[51] !== apy || $[52] !== hideNetApyHeader || $[53] !== netApy || $[54] !== rewards?.length || $[55] !== variant) {
        t17 = variant === "vault" || hideNetApyHeader || (rewards?.length ?? 0) > 0 || isDefined(apy) && !isDefined(netApy);
        $[51] = apy;
        $[52] = hideNetApyHeader;
        $[53] = netApy;
        $[54] = rewards?.length;
        $[55] = variant;
        $[56] = t17;
      } else {
        t17 = $[56];
      }
      const showBreakdownRow = t17;
      const hasContentBelowHeader = showBreakdownRow || hasRateBreakdown || asset instanceof YieldBearingToken || asset instanceof VaultToken || hasCollateralRewards || (points?.length ?? 0) > 0;
      const hasMidContent = showBreakdownRow || hasRateBreakdown;
      showCappedRow = isVault && !isVaultV2 && isRateCapped;
      showDonationRow = isVault && !isVaultV2 && includesDonations;
      let t18;
      if ($[57] !== maxApy) {
        t18 = formatMaxApy(maxApy);
        $[57] = maxApy;
        $[58] = t18;
      } else {
        t18 = $[58];
      }
      formattedMaxApy = t18;
      if ($[59] !== apy || $[60] !== apyBreakdown || $[61] !== breakdownLabel || $[62] !== durationText || $[63] !== hasContentBelowHeader || $[64] !== headerLabel || $[65] !== hideNetApyHeader || $[66] !== includesDonations || $[67] !== isRateCapped || $[68] !== isVault || $[69] !== isVaultV2 || $[70] !== maxApy || $[71] !== maxRate || $[72] !== netApy || $[73] !== rewards || $[74] !== showBreakdownRow || $[75] !== variant) {
        apyCardRows = isVaultV2 ? mapVaultApyCardRows({
          breakdown: apyBreakdown ?? null,
          netApyExcludingRewards: apy === null || apy === undefined ? null : Number(apy) / 1000000000000000000,
          netApy: netApy == null ? null : Number(netApy) / 1000000000000000000,
          rewards: (rewards ?? []).map(_temp2),
          includesDonations: includesDonations ?? false,
          isRateCapped: isRateCapped ?? false,
          maxApy: maxApy ?? null
        }) : null;
        const displayedNetApy = isVaultV2 && apyCardRows ? apyCardRows.netApy === null ? null : safeParseNumber(apyCardRows.netApy) : netApy;
        const t19 = durationText ? `${durationText} ` : "";
        let t20;
        if ($[83] !== headerLabel || $[84] !== t19) {
          t20 = <span className="text-foreground text-sm">{t19}{headerLabel}</span>;
          $[83] = headerLabel;
          $[84] = t19;
          $[85] = t20;
        } else {
          t20 = $[85];
        }
        let t21;
        if ($[86] !== isVault || $[87] !== isVaultV2) {
          t21 = !isVaultV2 && <InfoTooltip tooltipContent={isVault ? "The instant net yield after factoring in rewards and curator fees." : "The net rate after factoring in rewards."} />;
          $[86] = isVault;
          $[87] = isVaultV2;
          $[88] = t21;
        } else {
          t21 = $[88];
        }
        let t22;
        if ($[89] !== t20 || $[90] !== t21) {
          t22 = <div className="gap-2xs flex items-center leading-none">{t20}{t21}</div>;
          $[89] = t20;
          $[90] = t21;
          $[91] = t22;
        } else {
          t22 = $[91];
        }
        netApyRow = <div className={rowClassName} data-testid={isVaultV2 ? "vault-v2-apy-net-row" : undefined}>{t22}<span className="text-foreground text-sm">{formatVaultApy(displayedNetApy) ?? "-"}</span></div>;
        t3 = hideNetApyHeader ? undefined : "px-sm py-sm";
        if ($[92] !== hasContentBelowHeader || $[93] !== hideNetApyHeader || $[94] !== isVault || $[95] !== netApyRow) {
          t4 = !hideNetApyHeader && !isVault && <>{netApyRow}{hasContentBelowHeader && <div className="my-1.5"><Separator /></div>}</>;
          $[92] = hasContentBelowHeader;
          $[93] = hideNetApyHeader;
          $[94] = isVault;
          $[95] = netApyRow;
          $[96] = t4;
        } else {
          t4 = $[96];
        }
        if ($[97] !== hideNetApyHeader || $[98] !== variant) {
          t5 = hideNetApyHeader && <div className="pb-sm flex min-h-8 items-center"><span className="text-foreground text-sm leading-none">{variant === "market" ? "Rate Breakdown" : "APY Breakdown"}</span></div>;
          $[97] = hideNetApyHeader;
          $[98] = variant;
          $[99] = t5;
        } else {
          t5 = $[99];
        }
        t6 = showBreakdownRow && <div className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<InterestGenerated />} className="text-secondary-foreground" /><span className="text-2xs text-secondary-foreground">{breakdownLabel}</span>{isVault && !isVaultV2 && <InfoTooltip tooltipContent={<>The instant base yield generated by the vault's strategy, net of all curator fees.</>} />}{isVault && !isVaultV2 && maxRate === ZERO && <VaultV2MaxRateWarning />}</div><span className="text-2xs text-secondary-foreground leading-none">{formatVaultApy(isVaultV2 && apyCardRows ? apyCardRows.displayed.native : apy) ?? "-"}</span></div>;
        t7 = isVaultV2 && apyCardRows?.boost && <div className={rowClassName} data-testid="vault-v2-apy-boost-row"><div className="gap-2xs flex items-center leading-none"><Icon icon={<CirclePlus />} className="text-secondary-foreground" /><span className="text-2xs text-secondary-foreground">{apyCardRows.boost.label}</span></div><span className="text-2xs text-secondary-foreground leading-none">+{formatVaultApy(apyCardRows.displayed.boost)}</span></div>;
        $[59] = apy;
        $[60] = apyBreakdown;
        $[61] = breakdownLabel;
        $[62] = durationText;
        $[63] = hasContentBelowHeader;
        $[64] = headerLabel;
        $[65] = hideNetApyHeader;
        $[66] = includesDonations;
        $[67] = isRateCapped;
        $[68] = isVault;
        $[69] = isVaultV2;
        $[70] = maxApy;
        $[71] = maxRate;
        $[72] = netApy;
        $[73] = rewards;
        $[74] = showBreakdownRow;
        $[75] = variant;
        $[76] = apyCardRows;
        $[77] = netApyRow;
        $[78] = t3;
        $[79] = t4;
        $[80] = t5;
        $[81] = t6;
        $[82] = t7;
      } else {
        apyCardRows = $[76];
        netApyRow = $[77];
        t3 = $[78];
        t4 = $[79];
        t5 = $[80];
        t6 = $[81];
        t7 = $[82];
      }
      if ($[100] !== apyCardRows || $[101] !== isVaultV2 || $[102] !== rewards) {
        t8 = rewards?.map((reward_1, index) => {
          const rewardChain = InterfaceChainUtils.tryGetChain(reward_1.rewardToken.chain.id) ?? null;
          return <div key={reward_1.rewardToken.address} className={rowClassName}><div className="gap-2xs flex items-center leading-none"><AvatarMultichain src={reward_1.rewardToken.logoURI} chain={rewardChain ? {
                id: reward_1.rewardToken.chain.id,
                name: rewardChain.name
              } : null} size="medium" /><span className="text-2xs text-secondary-foreground">{reward_1.rewardToken.symbol}</span>{rewardChain && <ExternalLink href={reward_1.merklOpportunityUrl ?? ChainUtils.getExplorerAddressUrl(reward_1.rewardToken.chain.id, reward_1.rewardToken.address)}><Button variant="ghost" size="iconSm" aria-label="View reward details"><ArrowDiagonalUp /></Button></ExternalLink>}</div><div className="gap-2xs flex items-center leading-none"><span className="text-2xs text-secondary-foreground">{rewardFormatter.of(isVaultV2 && apyCardRows ? apyCardRows.displayed.rewards[index] ?? reward_1.apr : reward_1.apr, 18)}</span></div></div>;
        });
        $[100] = apyCardRows;
        $[101] = isVaultV2;
        $[102] = rewards;
        $[103] = t8;
      } else {
        t8 = $[103];
      }
      if ($[104] !== dailyApy || $[105] !== hasRateBreakdown || $[106] !== instantApy || $[107] !== showBreakdownRow || $[108] !== weeklyApy) {
        t9 = hasRateBreakdown && <>{showBreakdownRow && <div className="my-1.5"><Separator /></div>}<div className={rowClassName}><span className="text-2xs text-secondary-foreground leading-none">Instant</span><span className="text-2xs text-secondary-foreground leading-none">{formatVaultApy(instantApy) ?? "-"}</span></div><div className={rowClassName}><span className="text-2xs text-secondary-foreground leading-none">24h average</span><span className="text-2xs text-secondary-foreground leading-none">{formatVaultApy(dailyApy) ?? "-"}</span></div><div className={rowClassName}><span className="text-2xs text-secondary-foreground leading-none">7D average</span><span className="text-2xs text-secondary-foreground leading-none">{formatVaultApy(weeklyApy) ?? "-"}</span></div></>;
        $[104] = dailyApy;
        $[105] = hasRateBreakdown;
        $[106] = instantApy;
        $[107] = showBreakdownRow;
        $[108] = weeklyApy;
        $[109] = t9;
      } else {
        t9 = $[109];
      }
      if ($[110] !== formattedPerformanceFee || $[111] !== isVault || $[112] !== isVaultV2) {
        t10 = isVault && !isVaultV2 && <div className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<Dollar />} className="text-secondary-foreground" /><span className="text-2xs text-secondary-foreground">Performance Fee</span><InfoTooltip tooltipContent={<>The performance fee is a percentage of the yield generated by the vault, aligning the curator's revenue with the vault's success.</>} /></div><Badge variant="tertiary" size="sm">{formattedPerformanceFee}</Badge></div>;
        $[110] = formattedPerformanceFee;
        $[111] = isVault;
        $[112] = isVaultV2;
        $[113] = t10;
      } else {
        t10 = $[113];
      }
      if ($[114] !== formattedManagementFee || $[115] !== isVault || $[116] !== isVaultV2 || $[117] !== managementFee) {
        t11 = isVault && !isVaultV2 && isDefined(managementFee) && <div className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<Dollar />} className="text-secondary-foreground" /><span className="text-2xs text-secondary-foreground whitespace-nowrap">Management Fee</span><InfoTooltip tooltipContent={<>The management fee is a continuous fee charged on the vault's total assets under management (AUM), providing a steady revenue stream for the vault's managers.</>} /></div><Badge variant="tertiary" size="sm">{formattedManagementFee}</Badge></div>;
        $[114] = formattedManagementFee;
        $[115] = isVault;
        $[116] = isVaultV2;
        $[117] = managementFee;
        $[118] = t11;
      } else {
        t11 = $[118];
      }
      if ($[119] !== asset || $[120] !== hasCollateralRewards || $[121] !== hasMidContent || $[122] !== points?.length) {
        t12 = hasMidContent && (asset instanceof YieldBearingToken || asset instanceof VaultToken || hasCollateralRewards || points?.length) ? <div className="my-1.5"><Separator /></div> : null;
        $[119] = asset;
        $[120] = hasCollateralRewards;
        $[121] = hasMidContent;
        $[122] = points?.length;
        $[123] = t12;
      } else {
        t12 = $[123];
      }
      if ($[124] !== asset || $[125] !== isVault) {
        t13 = (asset instanceof YieldBearingToken || asset instanceof VaultToken) && <NativeYield asset={asset} isVault={isVault} />;
        $[124] = asset;
        $[125] = isVault;
        $[126] = t13;
      } else {
        t13 = $[126];
      }
      t14 = hasCollateralRewards && <><div className="flex min-h-8 items-center"><span className="text-foreground text-sm leading-none">Collateral Rewards</span></div><p className="text-2xs text-secondary-foreground">Rewards on collateral deposited in this market.</p>{activeCollateralRewards.map(_temp3)}</>;
    }
    $[4] = apy;
    $[5] = apyBreakdown;
    $[6] = asset;
    $[7] = breakdownLabel;
    $[8] = collateralRewards;
    $[9] = dailyApy;
    $[10] = durationText;
    $[11] = formattedManagementFee;
    $[12] = formattedPerformanceFee;
    $[13] = headerLabel;
    $[14] = hideNetApyHeader;
    $[15] = includesDonations;
    $[16] = instantApy;
    $[17] = isRateCapped;
    $[18] = isVault;
    $[19] = isVaultV2;
    $[20] = managementFee;
    $[21] = maxApy;
    $[22] = maxRate;
    $[23] = netApy;
    $[24] = points?.length;
    $[25] = rewards;
    $[26] = variant;
    $[27] = weeklyApy;
    $[28] = apyCardRows;
    $[29] = formattedMaxApy;
    $[30] = netApyRow;
    $[31] = showCappedRow;
    $[32] = showDonationRow;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    apyCardRows = $[28];
    formattedMaxApy = $[29];
    netApyRow = $[30];
    showCappedRow = $[31];
    showDonationRow = $[32];
    t10 = $[33];
    t11 = $[34];
    t12 = $[35];
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
    t3 = $[39];
    t4 = $[40];
    t5 = $[41];
    t6 = $[42];
    t7 = $[43];
    t8 = $[44];
    t9 = $[45];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[127] !== points) {
    t16 = points && points.length > 0 && points.map(_temp4);
    $[127] = points;
    $[128] = t16;
  } else {
    t16 = $[128];
  }
  let t17;
  if ($[129] !== hideNetApyHeader || $[130] !== isVault || $[131] !== isVaultV2 || $[132] !== netApyRow) {
    t17 = isVault && (!hideNetApyHeader || isVaultV2) && <><div className="my-1.5"><Separator /></div>{netApyRow}</>;
    $[129] = hideNetApyHeader;
    $[130] = isVault;
    $[131] = isVaultV2;
    $[132] = netApyRow;
    $[133] = t17;
  } else {
    t17 = $[133];
  }
  let t18;
  if ($[134] !== apyCardRows || $[135] !== isVaultV2) {
    t18 = isVaultV2 && apyCardRows?.capCopy && <div className="bg-info mt-sm p-sm gap-xs flex items-start rounded-[12px]" data-testid="vault-v2-apy-cap-callout"><Icon icon={<ArrowUpToLine />} className="text-info-foreground mt-[2px] shrink-0" /><span className="text-2xs text-secondary-foreground">{apyCardRows.capCopy}</span></div>;
    $[134] = apyCardRows;
    $[135] = isVaultV2;
    $[136] = t18;
  } else {
    t18 = $[136];
  }
  let t19;
  if ($[137] !== apyCardRows?.hasPausedIcon || $[138] !== isVaultV2) {
    t19 = isVaultV2 && apyCardRows?.hasPausedIcon && <div className="bg-warning mt-sm p-sm gap-xs flex items-start rounded-[12px]" data-testid="vault-v2-apy-paused-callout"><Icon icon={<WarningTriangleSolid />} className="text-warning-foreground mt-[2px] shrink-0" /><span className="text-2xs text-secondary-foreground">{VAULT_V2_INTEREST_PAUSED_LABEL}. {VAULT_V2_INTEREST_PAUSED_TOOLTIP}</span></div>;
    $[137] = apyCardRows?.hasPausedIcon;
    $[138] = isVaultV2;
    $[139] = t19;
  } else {
    t19 = $[139];
  }
  let t20;
  if ($[140] !== formattedMaxApy || $[141] !== showCappedRow || $[142] !== showDonationRow) {
    t20 = (showDonationRow || showCappedRow) && <><div className="my-1.5"><Separator /></div>{showDonationRow && <div className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<RewardsIcon />} className="text-info-foreground" /><span className="text-3xs text-secondary-foreground">Includes donations</span><InfoTooltip tooltipContent={<>Part of this yield comes from donations paid in the vault's underlying asset, which can raise the APY above the market rate.</>} /></div></div>}{showCappedRow && <div className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<ArrowUpToLine />} className="text-info-foreground" /><span className="text-3xs text-secondary-foreground">Capped at {formattedMaxApy} max APY</span><InfoTooltip tooltipContent={getVaultV2RateCapTooltip(formattedMaxApy)} /></div></div>}</>;
    $[140] = formattedMaxApy;
    $[141] = showCappedRow;
    $[142] = showDonationRow;
    $[143] = t20;
  } else {
    t20 = $[143];
  }
  let t21;
  if ($[144] !== t10 || $[145] !== t11 || $[146] !== t12 || $[147] !== t13 || $[148] !== t14 || $[149] !== t16 || $[150] !== t17 || $[151] !== t18 || $[152] !== t19 || $[153] !== t20 || $[154] !== t3 || $[155] !== t4 || $[156] !== t5 || $[157] !== t6 || $[158] !== t7 || $[159] !== t8 || $[160] !== t9) {
    t21 = <div className={t3}>{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t16}{t17}{t18}{t19}{t20}</div>;
    $[144] = t10;
    $[145] = t11;
    $[146] = t12;
    $[147] = t13;
    $[148] = t14;
    $[149] = t16;
    $[150] = t17;
    $[151] = t18;
    $[152] = t19;
    $[153] = t20;
    $[154] = t3;
    $[155] = t4;
    $[156] = t5;
    $[157] = t6;
    $[158] = t7;
    $[159] = t8;
    $[160] = t9;
    $[161] = t21;
  } else {
    t21 = $[161];
  }
  return t21;
};
export default RewardsHoverCard;
function _temp(reward) {
  return reward.collateralApr !== 0;
}
function _temp2(reward_0) {
  return Number(reward_0.apr ?? ZERO) / 1000000000000000000;
}
function _temp3(reward_2) {
  const rewardChain_0 = InterfaceChainUtils.tryGetChain(reward_2.asset.chainId) ?? null;
  return <div key={reward_2.asset.address} className={rowClassName}><div className="gap-2xs flex items-center leading-none"><AvatarMultichain src={reward_2.asset.logoURI ?? null} chain={rewardChain_0 ? {
        id: reward_2.asset.chainId,
        name: rewardChain_0.name
      } : null} size="medium" /><span className="text-2xs text-secondary-foreground">{reward_2.asset.symbol}</span>{rewardChain_0 && <ExternalLink href={ChainUtils.getExplorerAddressUrl(reward_2.asset.chainId, reward_2.asset.address)}><Button variant="ghost" size="iconSm" aria-label="View on explorer"><ArrowDiagonalUp /></Button></ExternalLink>}</div><div className="gap-2xs flex items-center leading-none"><Icon icon={<RewardsGradient />} /><span className="text-2xs text-secondary-foreground">{collateralRewardFormatter.of(reward_2.collateralApr)}</span></div></div>;
}
function _temp4(point, index_0) {
  return <div key={`point-${index_0}`} className={rowClassName}><div className="gap-2xs flex items-center leading-none"><Icon icon={<Points />} className="text-secondary-foreground" />{point.label && <Badge variant="tertiary" size="sm">{point.label}</Badge>}<PointInfoHoverCard point={point} /></div><div className="gap-xs flex items-center leading-none">{point.value && <span className="text-2xs text-secondary-foreground">{point.value}</span>}{point.title && <span className="text-2xs text-foreground">{point.title}</span>}</div></div>;
}